import { useEffect, useState } from "react";
import {
  partnerGetWebhook,
  partnerSetWebhook,
  partnerSendTestWebhook,
} from "../../../services/partnerCalls";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle } from "./shared";
import { errorHandler } from "../../../utils";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import { Spinner } from "../../../components/UI/Spinner/Spinner";


export default function Webhooks() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookURL, setWebhookURL] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [testResult, setTestResult] = useState(null);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const res = await partnerGetWebhook();
      if (res?.data?.data) {
        setWebhookURL(res.data.data?.webhookUrl || null);
        setUrlInput(res.data.data?.webhookUrl || "");
        setRecentDeliveries(res.data.data?.recent_deliveries || []);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!urlInput.trim() || saving) return;
    setSaving(true);
    setTestResult(null);
    try {
      await partnerSetWebhook({ webhookUrl: urlInput.trim() });
      await fetchWebhooks();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!webhookURL || testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await partnerSendTestWebhook();
      const delivered = res?.delivered;
      setTestResult(delivered ? "delivered" : "failed");
      await fetchWebhooks();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  return (
    <div>
      <SectionTitle
        title="Webhooks"
        sub="Where we POST signed result payloads"
      />

      {loading && <Spinner className="my-10" />}

      <Panel className={s.pg5}>
        <div className={s.pg18}>
          Endpoint URL
        </div>
        <div className={s.pg19}>
          <input
            className={s.input}
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://your-app.example.com/webhooks/awasource"
          />
          <button
            type="button"
            className={s.btnOutline}
            disabled={saving || !urlInput.trim() || urlInput.trim() === webhookURL}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save URL"}
          </button>
          <button
            type="button"
            className={s.btnSolid}
            disabled={!webhookURL || testing || loading}
            onClick={handleSendTest}
          >
            {testing ? "Sending…" : "Send test event"}
          </button>
        </div>

        {testResult && (
          <div className={`${s.note} ${s.pg15}`} style={{ color: testResult === "delivered" ? "#22c55e" : "#f87171" }}>
            {testResult === "delivered"
              ? "Test event delivered successfully."
              : "Test event failed to deliver — check your endpoint's logs."}
          </div>
        )}

        <div className={`${s.note} ${s.pg15}`}>
          We POST a JSON payload to this URL for session/test events. Failed
          deliveries retry with backoff; if all retries fail, pull the result
          from <span className={s.pg14}>GET /v1/results/{"{id}"}</span>.
        </div>
      </Panel>

      <Panel flush>
        <div
          className={`${s.blockTitle} ${s.pg22}`}
        >
          Recent deliveries
        </div>
        {!loading && recentDeliveries?.length === 0 && (
          <div className="text-sm text-[#4a5a7a] flex my-20 mx-auto justify-center">
            You do not have any recent deliveries
          </div>
        )}
        {recentDeliveries?.length > 0 &&
          recentDeliveries?.map((log, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                padding: "12px 18px",
                borderBottom:
                  i < recentDeliveries?.length - 1
                    ? "1px solid #141e30"
                    : "none",
              }}
            >
              <div className={s.pg23}>
                <span className={`${s.tdMono} ${s.pg24}`}>
                  {log?.event}
                </span>
                <span className={`${s.tdMono} ${s.pg25}`}>
                  {log?.sess}
                </span>
              </div>
              <div className={s.pg26}>
                {log?.attempts > 1 && (
                  <span className={s.pg27}>
                    retried ✓
                  </span>
                )}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: log?.status === "success" ? "#22c55e" : "#f87171",
                  }}
                >
                  {log?.status}
                </span>
                <span className={s.pg28}>
                  {log?.sent_at}
                </span>
              </div>
            </div>
          ))}
      </Panel>
    </div>
  );
}
